/**
 * Display → UI scale resolution (plan overhaul §3.2; portrait plan v0.14.0 §S, P-1).
 *
 * Landscape (W ≥ H): one mode, fit by height. Units: device px (backbuffer) and stage px (layout
 * unit; 1 stage px = `f` device px). The stage is 1080 stage px tall whenever the window is at
 * least 4:3; the chrome always overlays it.
 *
 * Portrait (W < H): the stage is about one CSS px per stage px times the OS text factor `k`
 * (at least 1, or 1.1 at DPR ≥ 2.5, capped by the width: `k_eff = min(max(k, 1), cssW / 320)`), 320–1024 stage px wide, as tall as the
 * window. Pure: no DOM access, so it is unit-tested against every display.
 */

export interface Size {
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface ResolvedScale {
  /** Device px per stage px: `min(H/1080, W/1440)`. */
  f: number;
  /** Stage size in stage px, after the aspect clamp (height is always 1080). */
  stage: Size;
  /** Top-left of the stage in device px: letterbox/pillarbox + leftover border. */
  offset: Point;
  /** The full device area the stage was resolved for (device px). */
  device: Size;
  /** Window is taller than wide: the stage lays out in portrait (plan §S). */
  portrait: boolean;
  /** A touch-primary landscape window with a short side under `ROTATE_MAX_CSS` CSS px (a phone held
   *  sideways): the gate shows "ROTATE TO PORTRAIT" (plan P2). */
  rotate: boolean;
  /** Landscape: `f` under `MIN_SCALE` (≈ 960×540; a sideways phone is caught by `rotate` instead);
   *  portrait: under 320 CSS px wide, a stage flatter than `MIN_PORTRAIT_ASPECT` or under
   *  `MIN_PORTRAIT_H` tall. The gate shows. */
  tooSmall: boolean;
}

/** The reference stage: the v6 layout size, in stage px. */
export const REF_STAGE: Readonly<Size> = Object.freeze({ w: 1920, h: 1080 });

/** Stage aspect range: below it the stage letterboxes, above it pillarboxes. */
export const MIN_ASPECT = 4 / 3;
export const MAX_ASPECT = 4;

/** Under this scale (≈ 960×540) the UI shows "WINDOW TOO SMALL". */
export const MIN_SCALE = 0.5;
/** Landscape touch window with a short side under this (CSS px) shows "ROTATE TO PORTRAIT". */
export const ROTATE_MAX_CSS = 500;

/** Portrait stage width clamp (stage px) and the narrowest supported CSS width. */
export const PORTRAIT_MIN_W = 320;
export const PORTRAIT_MAX_W = 1024;
/** A portrait stage flatter than h = 1.3·w, or under 568 stage px tall (the iPhone SE), is too
 *  small for the header, the map with its PIP, toast and captions, and the sheet peek (which grows
 *  to 212 for Monza's credit at 320 wide); the floor binds only under w ≈ 437. */
export const MIN_PORTRAIT_ASPECT = 1.3;
export const MIN_PORTRAIT_H = 568;
/** r141 (owner): a portrait screen at DPR ≥ 2.5 treats the OS text size as at least 1.1 — the HUD is 10 % larger */
export const HIGH_DPR = 2.5;
export const HIGH_DPR_K_MIN = 1.1;

/**
 * Resolve UI scale, stage size and stage placement for a device area at device pixel ratio `dpr`
 * with the OS text factor `k` (portrait only; landscape always uses 1) and `coarse` = the primary
 * pointer is touch. Returns `null` for degenerate sizes (< 1 device px or non-finite): callers keep
 * the previous result (e.g. a minimised window).
 */
export function resolveScale(Wdev: number, Hdev: number, dpr = 1, k = 1, coarse = false): ResolvedScale | null {
  if (!(Number.isFinite(Wdev) && Number.isFinite(Hdev)) || Wdev < 1 || Hdev < 1) return null;
  const px = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  if (Wdev < Hdev) return resolvePortrait(Wdev, Hdev, px, Number.isFinite(k) && k > 0 ? k : 1);
  const h = REF_STAGE.h;
  // aspect ≥ 4:3: height decides; narrower than 4:3 the width limit wins (letterbox)
  const f = Math.min(Hdev / h, Wdev / (h * MIN_ASPECT));
  const w = Math.min(floorEps(Wdev / f), Math.floor(h * MAX_ASPECT)); // never overrun Wdev
  return {
    f,
    stage: { w, h },
    offset: { x: Math.max(0, Math.floor((Wdev - w * f) / 2)), y: Math.max(0, Math.floor((Hdev - h * f) / 2)) },
    device: { w: Wdev, h: Hdev },
    portrait: false,
    rotate: coarse && Hdev / px < ROTATE_MAX_CSS,
    tooSmall: f < MIN_SCALE,
  };
}

/** The portrait branch (plan §S): `w = clamp(round(cssW / k_eff), 320, 1024)`, `f = W / w`, `h = ⌊H / f⌋`.
 *  `k_eff` is at least 1 (lead, r14 SF3): a smaller OS text size keeps the default, so a 44 stage-px
 *  touch target is never under 44 CSS px; a larger one still applies. At DPR ≥ `HIGH_DPR` the floor is
 *  `HIGH_DPR_K_MIN` (r141). */
function resolvePortrait(Wdev: number, Hdev: number, dpr: number, k: number): ResolvedScale {
  const cssW = Wdev / dpr;
  const kEff = Math.min(Math.max(k, dpr >= HIGH_DPR ? HIGH_DPR_K_MIN : 1), cssW / PORTRAIT_MIN_W);
  const w = Math.min(Math.max(Math.round(cssW / kEff), PORTRAIT_MIN_W), PORTRAIT_MAX_W);
  const f = Wdev / w;
  const h = floorEps(Hdev / f);
  return {
    f,
    stage: { w, h },
    offset: { x: 0, y: Math.max(0, Math.floor((Hdev - h * f) / 2)) },
    device: { w: Wdev, h: Hdev },
    portrait: true,
    rotate: false,
    tooSmall: cssW < PORTRAIT_MIN_W - 1e-9 || h < MIN_PORTRAIT_ASPECT * w || h < MIN_PORTRAIT_H,
  };
}

/** The stage's extent in device px (integer). */
export function stageDeviceSize(r: ResolvedScale): Size {
  return { w: Math.min(Math.round(r.stage.w * r.f), r.device.w), h: Math.min(Math.round(r.stage.h * r.f), r.device.h) };
}

/** floor() that tolerates float error just below an integer (e.g. 1600 / (1600/1440) = 1439.9999…). */
function floorEps(x: number): number {
  return Math.floor(x + 1e-9);
}
